import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { FaHistory } from 'react-icons/fa'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import {
  parseParametrizatioIncrementSupplierData,
  parseParametrizatioProductSimilarData
} from '@helper/grid'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import { IDefaultDate } from '@base/@types/providers/Alert/FollowUpProvider'
import { IIncrementSupplierContext } from '@base/@types/providers/Parametrization/IncrementSupplierProvider'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/IncrementSupplierProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationIncrementSupplierHeader from '@base/variables/headers/parametrization/increment-supplier'

const ParametrizationIncrementSupplierProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Incremento Estoque - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)

  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [pageCurrent, setPageCurrent] = useState(1)
  const [total, setTotal] = useState(10)
  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)
  const [selecteds, setSelecteds] = useImmer([] as any)
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    lojaIds: [],
    produtoIds: [],
    dataInicio: new Date().toDateString(),
    dataFim: new Date().toDateString(),
    estoqueDias: 0,
    incrementoEstoque: 0,
    observation: ''
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )

  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getStore()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const initialDate = dayjs(dateNow).subtract(1, 'year').format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).add(1, 'year').format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    produtosIds: [],
    empresaIds: [],
    periodo: new Date().toISOString(),
    periodoFim: new Date().toISOString(),
    initialDate: defaultDate.initialDate,
    finalDate: defaultDate.finalDate
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationIncrementSupplierHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationIncrementSupplierHeader')) {
      encrypteHeaderStorange('parametrizationIncrementSupplierHeader', headers)
    } else {
      localStorage.removeItem('parametrizationIncrementSupplierHeader')
      encrypteHeaderStorange('parametrizationIncrementSupplierHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {
    const configuracaoGrid = {
      id: headers[0].columns[0] ? headers[0].columns[0].show : true,
      empresaId: headers[1].columns[0] ? headers[1].columns[0].show : true,
      empresaNome: headers[1].columns[1] ? headers[1].columns[1].show : true,
      produtoNovoId: headers[2].columns[0] ? headers[2].columns[0].show : true,
      produtoNovoNome: headers[2].columns[1]
        ? headers[2].columns[1].show
        : true,
      produtoSimilarId: headers[3].columns[0]
        ? headers[3].columns[0].show
        : true,
      produtoSimilarNome: headers[3].columns[1]
        ? headers[3].columns[1].show
        : true,
      percentualDemandaAbsorvida: headers[4].columns[0]
        ? headers[4].columns[0].show
        : true
    }
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      configuracaoGrid
    }
    try {
      const { data: res } = await parametrization.postExportSimilarProduct(
        paramsToSend
      )
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Erro Requisição.',
        description: message,
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [toast, filename, headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationIncrementSupplierHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationIncrementSupplierHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    if (name === 'initialDate') {
      setCurrentyDate({
        initialDate: value,
        finalDate: currentyDate.finalDate
      })
    }
    if (name === 'finalDate') {
      setCurrentyDate({
        initialDate: currentyDate.initialDate,
        finalDate: value
      })
    }

    updateParams(draft => {
      draft[name] = value
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {}, [])

  const getData = useCallback(
    async (search: any, page: any, total: any, isQuery: boolean) => {
      setLoading(!(page > 1))

      _setFetching(true)

      try {
        const paramsToSend = {
          lojaIds: paramsHelper.parse(params.lojasIds),
          produtoIds: paramsHelper.parse(params.produtosIds),
          empresaIds: paramsHelper.parse(params.empresaIds),
          dataInicio: currentyDate.initialDate,
          dataFim: currentyDate.finalDate,
          search: search === null ? '' : search
        }
        // await getMaintence()

        encrypteHeaderStorange(
          'parametrizationIncrementSupplierHeader',
          headers
        )

        handleStorange()
        parametrization
          .getParamsIncrementSupplier(search, page, 50, paramsToSend)
          .then(response => {
            const newData = [...data, ...response.data?.data]

            if (isQuery || page === 1) {
              updateData(response.data?.data)
            } else {
              updateData(newData)
            }
            setLoading(false)
            _setFetching(false)

            const pagination = response.data.pagination
            const xTotal = pagination.totalRows
            updatePagination(response.data.pagination)
            setTotal(xTotal)
            setPageCurrent(page)
            // setSelectedOne(0)
            // setSelecteds([])
          })
          // .then(({ data: { data, pagination } }) => {
          //   updateData(draft => {
          //     return draft.filter(item => item.status === 99).concat(data)
          //   })

          //   updatePagination(pagination)
          //   setLoading(false)
          //   const xTotal = response.headers['x-total-count']
          //   setTotal(xTotal)
          // })
          .catch(() => {
            updatePagination({
              actualPage: 0,
              rows: 0,
              totalPages: 0,
              totalRows: 0,
              skip: 0
            })
            updateData([])
            setLoading(false)
            _setFetching(false)
          })
      } catch {
        setLoading(false)
        _setFetching(false)
      }
    },
    [
      params.lojasIds,
      params.produtosIds,
      params.empresaIds,
      currentyDate.initialDate,
      currentyDate.finalDate,
      headers,
      handleStorange,
      data,
      updatePagination,
      updateData
    ]
  )

  const updateDataIncrement = useCallback(
    async (ype: boolean) => {
      try {
        const { data: _res } =
          await parametrization.postParamsIncrementSupplier({
            lojaIds: paramsEdit.lojaIds.map(item => item.value),
            produtoIds: paramsEdit.produtoIds.map(item => item.value),
            dataInicio: paramsEdit.dataInicio,
            dataFim: paramsEdit.dataFim,
            estoqueDias: ype ? Number(paramsEdit.estoqueDias) : null,
            incrementoEstoque: !ype
              ? Number(paramsEdit.incrementoEstoque)
              : null,
            observacao: paramsEdit.observation
          })
        toast({
          title: 'Sucesso.',
          description: 'Incremento de Estoque editado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      }
      setLoading(false)
      setSelectedOne(0)
      setSelecteds([])

      getData(null, 1, 50, false)
    },
    [
      setSelecteds,
      getData,
      paramsEdit.lojaIds,
      paramsEdit.produtoIds,
      paramsEdit.dataInicio,
      paramsEdit.dataFim,
      paramsEdit.estoqueDias,
      paramsEdit.incrementoEstoque,
      paramsEdit.observation,
      toast
    ]
  )

  const deleteData = useCallback(async () => {
    try {
      await parametrization.removeParamsIncrementSupplier({
        ids: selecteds
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString(),
        estoqueDias: 0,
        incrementoEstoque: 0,
        observation: ''
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    setSelecteds([])
    getData(null, 1, 50, false)
  }, [setSelecteds, getData, selecteds, toast, updateEditParams])

  const createData = useCallback(
    async (ype: boolean) => {
      try {
        await parametrization.postParamsIncrementSupplier({
          lojaIds: paramsEdit.lojaIds.map(item => item.value),
          produtoIds: paramsEdit.produtoIds.map(item => item.value),
          dataInicio: new Date(paramsEdit.dataInicio),
          dataFim: new Date(paramsEdit.dataFim),
          estoqueDias: ype ? Number(paramsEdit.estoqueDias) : null,
          incrementoEstoque: !ype ? Number(paramsEdit.incrementoEstoque) : null,
          observacao: paramsEdit.observation
        })
        toast({
          title: 'Sucesso.',
          description: 'Incremento de Estoque cadastrado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        updateEditParams({
          lojaIds: [],
          produtoIds: [],
          dataInicio: new Date().toDateString(),
          dataFim: new Date().toDateString(),
          estoqueDias: 0,
          incrementoEstoque: 0,
          observation: ''
        })
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      }
      setLoading(false)
      setSelectedOne(0)
      setSelecteds([])
      getData(null, 1, 50, false)
    },
    [
      getData,
      paramsEdit.dataFim,
      paramsEdit.dataInicio,
      paramsEdit.estoqueDias,
      paramsEdit.incrementoEstoque,
      paramsEdit.lojaIds,
      paramsEdit.observation,
      paramsEdit.produtoIds,
      setSelecteds,
      toast,
      updateEditParams
    ]
  )

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      {
        id: 'empresaIds',
        label: 'Empresas',
        filterId: 'companies',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produtos',
        type: 'select-api-post',
        apiUrl: '/v1/Produto',
        isMulti: true
      },

      {
        id: 'periodo',
        label: 'Datas',
        type: 'date-input',
        isMulti: false
      }
    ]
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterIncrementProduct',
            data,
            headers,
            parser: parseParametrizatioProductSimilarData
          }
        ]
      },
      {
        type: 'parametrizationIncrementSupplyUpload'
      }
    ]
  }, [data, headers])

  const handleSelectAll = () => {
    if (selecteds.length < data.length) {
      const res = []
      for (const item of data) {
        res.push(item.id)
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'lojaIds',
          value: [
            {
              label: idData[0].lojaId
                ? idData[0].lojaId + ' - ' + idData[0].lojaDescricao
                : '',
              value: idData[0].lojaId
            }
          ]
        })
        handleUpdateEditParams({
          name: 'produtoIds',
          value: [
            {
              label: idData[0].produtoId
                ? idData[0].produtoId + ' - ' + idData[0].produtoDescricao
                : '',
              value: idData[0].produtoId
            }
          ]
        })

        handleUpdateEditParams({
          name: 'estoqueDias',
          value:
            idData[0]?.estoqueDias != null ? Number(idData[0].estoqueDias) : 0
        })
        handleUpdateEditParams({
          name: 'incrementoEstoque',
          value:
            idData[0]?.incrementoEstoque != null
              ? Number(idData[0].incrementoEstoque)
              : 0
        })
        handleUpdateEditParams({
          name: 'dataInicio',
          value: new Date(idData[0].dataInicio).toISOString().split('T')[0]
        })
        handleUpdateEditParams({
          name: 'dataFim',
          value: new Date(idData[0].dataFim).toISOString().split('T')[0]
        })
        handleUpdateEditParams({
          name: 'observation',
          value: idData[0].observacao ? idData[0].observacao : ''
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const postUpload = useCallback(
    async (event: any) => {
      setLoading(true)

      parametrization
        .uploadSupplyIncrement({
          file: event.target.files[0]
        })
        .then(() => {
          getData(null, 1, 50, false)
          toast({
            title: 'Sucesso',
            description: 'Alterações realizadas com Sucesso',
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          setLoading(false)
        })
        .catch(response => {
          let htmlLines = ''
          if (
            response?.response?.data?.message !==
            'O arquivo não possui informações'
          ) {
            for (
              let aux = 0;
              aux < response?.response?.data?.data?.length;
              aux++
            ) {
              htmlLines += '<li>' + response?.response?.data?.data[aux] + '</>'
            }
            Swal.fire({
              title: '<strong>Ops, houve um error</u></strong>',
              icon: 'error',
              html: 'Verificar os erros abaixo, ' + htmlLines,
              showCloseButton: true,
              showCancelButton: true,
              focusConfirm: false
            })
          } else {
            const message =
              response.response?.status === 400
                ? response.response?.data?.message
                : 'Ocorreu um problema interno. Tente novamente mais tarde'
            toast({
              title: 'Erro Requisição.',
              description: message,
              status: 'error',
              duration: 2000,
              isClosable: true
            })
          }

          setLoading(false)
        })
    },
    [toast, setLoading, getData]
  )
  const handleSelect = useCallback(
    async (index: any) => {
      const supply = index['id-id']
      let verifyUpdate = selecteds.length
      if (!selecteds.includes(supply)) {
        verifyUpdate++
        setSelecteds([...selecteds, supply])
      } else {
        verifyUpdate--
        setSelecteds(selecteds.filter((prop: any) => prop !== supply))
      }
      if (verifyUpdate === 1) {
        handleEditParams(index['id-id'])
      } else {
        updateEditParams({
          lojaIds: [],
          produtoIds: [],
          dataInicio: new Date().toDateString(),
          dataFim: new Date().toDateString(),
          estoqueDias: 0,
          incrementoEstoque: 0,
          observation: ''
        })
      }
    },
    [selecteds, setSelecteds, handleEditParams, updateEditParams]
  )
  // const handleSelect = useCallback(
  //   async (index: any) => {
  //     const similarProduct = index['id-id']
  //     const newSelected = selected

  //     setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
  //     if (newSelected === similarProduct) {
  //       // updateEditParams({
  //       //   empresa: 0,
  //       //   produtoNovo: 0,
  //       //   produtoSimilar: 0,
  //       //   politicaDeEstoqueArbitraria: 0,
  //       //   cdDeOrigem: [],
  //       //   demanda: 0
  //       // })
  //     } else {
  //       handleEditParams(index['id-id'])
  //     }
  //   },
  //   [setSelectedOne, handleEditParams, selected, updateEditParams]
  // )

  // const tableInteraction = useMemo<ITableGridAction[]>(() => {
  //   return [
  //     {
  //       type: 'checkbox',
  //       icon: <FaHistory />,
  //       onChange: handleSelect,
  //       selecteds: selecteds,
  //       isMulti: true
  //     }
  //   ]
  // }, [selecteds, handleSelect])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: true
      }
    ]
  }, [handleSelect, selecteds])

  const clearAllFilters = () => {
    updateParams({
      lojasIds: [],
      produtosIds: [],
      empresaIds: [],
      periodo: new Date().toISOString(),
      periodoFim: new Date().toISOString(),
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
  }

  return (
    <ParametrizationIncrementSupplierContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioIncrementSupplierData(data),
        dataOriginal: data,
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selecteds,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        postUpload,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        defaultDate,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        handleSelectAll,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        currentyDate,
        handleToogleTable,
        updateDataIncrement,
        deleteData,
        setFilters,
        setFilename,
        clearAllFilters,
        pageCurrent,
        total
      }}
    >
      {children}
    </ParametrizationIncrementSupplierContext.Provider>
  )
}

export default ParametrizationIncrementSupplierProvider
export const ParametrizationIncrementSupplierContext =
  createContext<IIncrementSupplierContext>({} as IIncrementSupplierContext)
